import { Fingerprint, ShieldAlert } from 'lucide-react'
import { useState } from 'react'

import type { ApprovalAuditEntry, ApprovalAuditOutcome } from '@/api/approvals-types'
import { ErrorSurface } from '@/components/error-surface'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDateTime } from '@/lib/datetime'

import { APPROVAL_PERMISSIONS, useApprovalPermissions } from './permissions'
import { useApprovalTrail, useCheckerReach } from './queries'

/**
 * What has happened to one request.
 *
 * This is the whole of the history the API exposes. The trail is addressed by entity — the pair is
 * `ApprovalRequest` and the request id — and is deliberately not searchable, so there is no "every
 * request this month" to render and this panel never pretends otherwise.
 *
 * It is also branch-filtered per reader, and that matters more here than anywhere else the trail is
 * shown. `AuditWriter` stamps the acting user's own branch on each row, so a request's
 * `submitted_for_approval` row carries the maker's branch while its `approved`, `rejected` and
 * `approval_denied` rows carry the checker's. A reviewer holding `ops.audit.view` without
 * cross-branch reach can therefore be served a genuinely partial trail — with `totalCount` already
 * reduced and nothing in the body marking the gap. On a compliance surface that is the one wrong
 * answer that must not be given silently, so it is said in words above the entries.
 */

const OUTCOME_TONES: Record<ApprovalAuditOutcome, 'success' | 'danger' | 'warning' | 'neutral'> = {
  Succeeded: 'success',
  Failed: 'danger',
  Denied: 'warning',
  Unknown: 'neutral',
}

/**
 * The engine's own action codes, which are the request type plus a suffix. Anything else is shown
 * as its code: this trail belongs to the approval request, and a row on it that the engine did not
 * write is worth noticing rather than smoothing over.
 */
const ACTION_LABELS: Record<string, string> = {
  submitted_for_approval: 'Submitted for approval',
  approved: 'Countersigned, and the command ran',
  rejected: 'Refused by a checker',
  approval_denied: 'Countersignature refused by the engine',
  approval_expired: 'Lapsed without a decision',
}

function describeAction(actionCode: string): { label: string; code: string } {
  for (const [suffix, label] of Object.entries(ACTION_LABELS)) {
    if (actionCode.endsWith(`.${suffix}`)) {
      return { label, code: actionCode }
    }
  }

  return { label: actionCode, code: actionCode }
}

export interface ApprovalTrailProps {
  requestId: string
  /** Fired only when a panel showing it is open: reading a trail is itself an audited action. */
  enabled: boolean
}

export function ApprovalTrail({ requestId, enabled }: ApprovalTrailProps) {
  const permissions = useApprovalPermissions()
  const reach = useCheckerReach()
  const [page, setPage] = useState(1)

  const query = useApprovalTrail(requestId, page, enabled && permissions.canViewTrail)

  if (!permissions.canViewTrail) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-card border border-border-subtle bg-surface px-4 py-10 text-center">
        <ShieldAlert className="size-5 text-warning-text" aria-hidden />
        <p className="text-sm font-medium text-heading">The trail is restricted</p>
        <p className="max-w-sm text-xs text-muted">
          Reading what has happened to a request needs{' '}
          <span className="font-mono">{APPROVAL_PERMISSIONS.trailView}</span>. Deciding the request
          does not — the two are separate on purpose.
        </p>
      </div>
    )
  }

  // Computed, because this endpoint sends no totalPages — and guarded on the divisor, because a
  // pageSize of zero would make it Infinity and leave Next enabled for ever.
  const totalPages =
    query.data && query.data.pageSize > 0
      ? Math.max(1, Math.ceil(query.data.totalCount / query.data.pageSize))
      : 1

  return (
    <div className="space-y-3">
      {!reach.canAccessAllBranches ? (
        <Alert tone="warning">
          <Fingerprint aria-hidden />
          <div className="space-y-1">
            <AlertTitle>This trail may not be complete</AlertTitle>
            <AlertDescription>
              The trail is filtered to the branches you can reach, and a request&apos;s entries are
              stamped with the branch of whoever wrote them — the maker&apos;s on submission, the
              checker&apos;s on the decision. If the two are different branches you will see one and
              not the other, and the count below will already have been reduced. A complete history
              needs a reader with cross-branch reach.
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      {query.isPending ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-24 w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <ErrorSurface
          error={query.error}
          onRetry={() => void query.refetch()}
          title="The trail could not be read"
        />
      ) : query.data.items.length === 0 ? (
        <p className="rounded-card border border-border-subtle bg-surface px-4 py-10 text-center text-sm text-muted">
          Nothing is recorded against this request id. Either it has never existed, or every entry
          belongs to a branch outside your reach.
        </p>
      ) : (
        <>
          <ol className="space-y-2">
            {query.data.items.map((entry) => (
              <TrailEntry key={entry.id} entry={entry} />
            ))}
          </ol>

          <div className="flex items-center justify-between gap-2">
            <span className="numeric text-xs text-muted tabular-nums">
              {/* Computed from the count: this endpoint sends no totalPages. */}
              Page {String(query.data.page)} of {String(totalPages)} · {String(query.data.totalCount)}{' '}
              {query.data.totalCount === 1 ? 'entry' : 'entries'}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function TrailEntry({ entry }: { entry: ApprovalAuditEntry }) {
  const action = describeAction(entry.actionCode)

  return (
    <li className="rounded-card border border-border-subtle bg-surface p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-heading">{action.label}</p>
          <p className="font-mono text-2xs text-muted">{action.code}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <Badge tone={OUTCOME_TONES[entry.outcome]}>{entry.outcome}</Badge>
          <Badge tone="neutral">{entry.source}</Badge>
        </div>
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2">
        <TrailField label="When" value={formatDateTime(entry.occurredAtUtc)} />
        <TrailField label="Acted by" value={entry.actorUsername} />
        {/*
          Absent and null are different facts here. An entry with no approver is an action nobody
          countersigned; an approver id with no username is an account that has since been deleted,
          and saying "—" for both would merge the two.
        */}
        <TrailField
          label="Countersigned by"
          value={
            entry.approverUserId === null
              ? 'Not countersigned'
              : (entry.approverUsername ?? `User ${entry.approverUserId}, account since removed`)
          }
        />
        <TrailField
          label="Branch"
          value={entry.branchId === null ? 'No branch — a job or a sign-in' : `Branch ${entry.branchId}`}
        />
        {entry.failureReason ? (
          <div className="col-span-2 min-w-0">
            <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">Reason</dt>
            <dd className="mt-0.5 font-mono text-xs break-words text-danger-text">
              {entry.failureReason}
            </dd>
          </div>
        ) : null}
        <div className="col-span-2 min-w-0">
          <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">Correlation</dt>
          <dd className="mt-0.5 font-mono text-2xs break-all text-muted">{entry.correlationId}</dd>
        </div>
      </dl>

      {entry.beforeJson !== null || entry.afterJson !== null ? (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs font-medium text-brand-text max-lg:min-h-11 max-lg:py-2">
            The recorded change
          </summary>
          <div className="mt-2 space-y-2">
            {entry.beforeJson !== null ? <JsonBlock label="Before" json={entry.beforeJson} /> : null}
            {entry.afterJson !== null ? <JsonBlock label="After" json={entry.afterJson} /> : null}
          </div>
        </details>
      ) : null}
    </li>
  )
}

function TrailField({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</dt>
      <dd className="mt-0.5 text-sm break-words text-body">{value}</dd>
    </div>
  )
}

/**
 * The stored change, as text.
 *
 * Never parsed and re-rendered as fields. Sensitive values are masked on the server, twice, and
 * anything this screen did to the text afterwards would be a second copy of the evidence that
 * disagrees with the first. It scrolls inside its own box rather than widening the panel.
 */
function JsonBlock({ label, json }: { label: string; json: string }) {
  return (
    <div>
      <p className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</p>
      <pre className="mt-1 max-h-48 overflow-auto rounded-control bg-surface-muted p-2 font-mono text-2xs text-body">
        {json}
      </pre>
    </div>
  )
}
